import {ApplicationRef, ComponentRef, createComponent, EnvironmentInjector} from '@angular/core';
import {BasePortalHost, ComponentPortal} from './portal';

/**
 * A PortalHost for attaching portals to an arbitrary DOM element outside of the Angular
 * application context.
 *
 * This is the only part of the portal core that directly touches the DOM.
 */
export class DomPortalHost extends BasePortalHost {
  /**
   * A PortalHost for attaching portals to an arbitrary DOM element outside of the Angular
   * application context.
   *
   * This is the only part of the portal core that directly touches the DOM.
   */
  constructor(
    private readonly hostDomElement: Element,
    private readonly environmentInjector: EnvironmentInjector,
    private readonly appRef: ApplicationRef
  ) {
    super();
  }

  /**
   * Attach the given ComponentPortal to the DOM element.
   * @param portal Portal to be attached
   * @param newestOnTop
   */
  attachComponentPortal<T>(portal: ComponentPortal<T>, newestOnTop: boolean): ComponentRef<T> {
    const componentRef = createComponent(portal.component, {
      environmentInjector: this.environmentInjector,
      elementInjector: portal.injector,
    });
    this.appRef.attachView(componentRef.hostView);

    this.setDisposeFn(() => {
      this.appRef.detachView(componentRef.hostView);
      componentRef.destroy();
    });

    // At this point the component has been instantiated, so we move it to the location in the DOM
    // where we want it to be rendered.
    if (newestOnTop) {
      this.hostDomElement.insertBefore(componentRef.location.nativeElement, this.hostDomElement.firstChild);
    } else {
      this.hostDomElement.appendChild(componentRef.location.nativeElement);
    }

    return componentRef;
  }
}
